import React from "react";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Copyright,
  Send
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Don't render footer if user is authenticated
  if (isAuthenticated) {
    return null;
  }

  const exploreLinks = [
    { label: "Home", path: "/" },
    { label: "Services", path: "/services" },
    { label: "About Us", path: "/about" },
    // { label: "Blog", path: "/blog" },
    // { label: "Testimonial", path: "/testimonials" },
    { label: "Contact Us", path: "/contact" },
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms of Use", path: "/terms-of-use" },
    { label: "Refund Policy", path: "/refund-cancellation-policy" },
    { label: "Shipping Policy", path: "/shipping-delivery-policy" },
  ];

  const socialIcons = [
    { Icon: Facebook, href: "#" },
    { Icon: Twitter, href: "#" },
    { Icon: Instagram, href: "#" },
    { Icon: Linkedin, href: "#" },
  ];

  return (
    <footer className="relative bg-[#102147] text-white overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#44D2F6]/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#0C7FA5]/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/4" />
      </div>

      <div className="container mx-auto px-4 pt-20 pb-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Brand & Newsletter */}
          <div className="space-y-6">
            <Link to="/" className="block">
              <img src="/investhivelogo.webp" alt="InvestHive" className="h-12 w-auto brightness-0 invert" />
            </Link>
            <p className="text-gray-400 leading-relaxed">
              Empowering your financial future with smart investment strategies and expert guidance. Join thousands of satisfied investors today.
            </p>

            <div className="relative">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full bg-white/5 border border-white/10 rounded-full py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:border-[#44D2F6] focus:ring-1 focus:ring-[#44D2F6] transition-all"
              />
              <button className="absolute right-1 top-1 bottom-1 bg-[#44D2F6] hover:bg-[#0C7FA5] text-[#102147] rounded-full w-10 h-10 flex items-center justify-center transition-colors duration-300">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 flex items-center">
              <span className="w-8 h-1 bg-[#44D2F6] mr-3 rounded-full"></span>
              Explore
            </h3>
            <ul className="grid grid-cols-1 gap-3">
              {exploreLinks.slice(0, 4).map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-[#44D2F6] transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 -ml-6 group-hover:ml-0 transition-all duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies & Legal */}
          <div>
            <h3 className="text-xl font-bold mb-6 flex items-center">
              <span className="w-8 h-1 bg-[#44D2F6] mr-3 rounded-full"></span>
              Legal
            </h3>
            <ul className="grid grid-cols-1 gap-3">
              {exploreLinks.slice(4).map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-[#44D2F6] transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 -ml-6 group-hover:ml-0 transition-all duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-6 flex items-center">
              <span className="w-8 h-1 bg-[#44D2F6] mr-3 rounded-full"></span>
              Contact Us
            </h3>
            <div className="space-y-4">
              <div className="flex items-start text-gray-400 group hover:text-white transition-colors duration-300">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 mr-4 group-hover:bg-[#44D2F6] group-hover:text-[#102147] transition-all duration-300">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-white mb-1">Visit Us</p>
                  <p className="text-sm">Delhi & Pune, India</p>
                </div>
              </div>

              <a href="mailto:support@investhive.io" className="flex items-start text-gray-400 group hover:text-white transition-colors duration-300">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 mr-4 group-hover:bg-[#44D2F6] group-hover:text-[#102147] transition-all duration-300">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-white mb-1">Email Us</p>
                  <p className="text-sm">support@investhive.io</p>
                </div>
              </a>

              <a href="tel:+919582824578" className="flex items-start text-gray-400 group hover:text-white transition-colors duration-300">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 mr-4 group-hover:bg-[#44D2F6] group-hover:text-[#102147] transition-all duration-300">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-white mb-1">Call Us</p>
                  <p className="text-sm">+91-95828 24578</p>
                </div>
              </a>
            </div>

            {/* Social Icons */}
            {/* <div className="flex gap-3 mt-8">
              {socialIcons.map(({ Icon, href }, index) => (
                <a
                  key={index}
                  href={href}
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-[#44D2F6] hover:text-[#102147] hover:-translate-y-1 transition-all duration-300"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div> */}
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10 relative z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Copyright className="w-4 h-4 mr-2" />
              <p>
                {new Date().getFullYear()} <span className="text-white font-medium">InvestHive</span>. All rights reserved.
              </p>
            </div>
            {/* <div className="flex gap-6">
              <Link to="/privacy-policy" className="hover:text-[#44D2F6] transition-colors">Privacy</Link>
              <Link to="/terms-of-use" className="hover:text-[#44D2F6] transition-colors">Terms</Link>
              <Link to="/sitemap" className="hover:text-[#44D2F6] transition-colors">Sitemap</Link>
            </div> */}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;